import axios from "axios";

const instance = axios.create({
  baseURL: `${process.env.REACT_APP_BASE_URL}`,
  timeout: 500000,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

// Refactored request interceptor
instance.interceptors.request.use((config) => {
  // Conditionally log for debugging in development environment
  // if (process.env.NODE_ENV === "development") {
  console.log("API Base URL:", process.env.REACT_APP_BASE_URL);
  // }

  // Check both session and local storage for the token, just like in authServices.js
  const token = localStorage.getItem("token");

  if (token) {
    // Modify headers on the existing config object
    config.headers.Authorization = `Bearer ${token}`;
    // The original code had a custom 'rlinks-token'. Correcting to 'rlink-token' for consistency.
    config.headers["rlink-token"] = token;
  }

  // Handle FormData - let browser set Content-Type with boundary
  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }

  return config;
});

instance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Check if we're on a meeting page
      // Meeting pages can be: /chris, /abc-def-ghi, /meeting-123, etc.
      const pathname = window.location.pathname;

      // List of routes that are NOT meeting pages
      const excludedRoutes = [
        '/dashboard',
        '/login',
        '/forgot-password',
        '/recovery',
        '/recoveryc',
        '/meeting-not-found',
        '/webiner-branding-pre',
        '/webiner-branding-waiting',
        '/webiner-branding-post',
        '/maintenance',
        '/recording-preview'
      ];

      // Check if path matches meeting room pattern (single segment with alphanumeric and dashes)
      // Match: /chris, /abc-def-ghi, /room123, etc.
      // Exclude: /dashboard/rooms, /login, etc.
      const isMeetingPage = /^\/[a-z0-9-]+$/i.test(pathname) &&
                           !excludedRoutes.some(route => pathname.startsWith(route));

      console.log("[Auth] 401 error - pathname:", pathname, "isMeetingPage:", isMeetingPage, "url:", error.config?.url);

      // Only clear tokens and redirect if NOT on a meeting page
      // Meeting pages should maintain their session even if backend auth fails
      if (!isMeetingPage) {
        console.warn("[Auth] Redirecting to login due to 401 on non-meeting page");
        // Clear the invalid token
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/login";
      } else {
        // On meeting pages, log the error but don't clear tokens or redirect
        // This allows users to continue their meeting session, even with 401 errors
        console.warn("[Auth] 401 error on meeting page - maintaining session:", error.config?.url);
      }
    }
    return Promise.reject(error);
  }
);

const responseBody = (response) => response.data;

const requests = {
  get: (url) => instance.get(url).then(responseBody),

  blob: (url) => instance.get(url, { responseType: "blob" }).then(responseBody),

  post: (url, body, config) =>
    instance.post(url, body, config).then(responseBody),

  put: (url, body) => instance.put(url, body).then(responseBody),

  delete: (url, config) => instance.delete(url, config).then(responseBody),

  patch: (url, body, config) =>
    instance.patch(url, body, config).then(responseBody),
};

// Helper function to get API base URL
export const getApiUrl = () => {
  return process.env.REACT_APP_BASE_URL || "";
};

export default requests;
